// Centralized getLabel() keys for the Event Alerts feature (area "private",
// same flat dictionary as the rest of the app - see FE0 discovery report).
// These keys do not exist yet in the backend dictionary; a separate backend
// micro-patch will add the it/en strings after FE1. Until then getLabel()
// returns its own "#locale_area_key" sentinel for all of them, which is the
// expected/normal state during FE1 development and tests.

export const EVENT_ALERT_LABEL_KEYS = {
  SECTION_TITLE: "eventAlerts",
  STATUS: {
    OPEN: "eventAlertOpen",
    ACKNOWLEDGED: "eventAlertAcknowledged",
    RESOLVED: "eventAlertResolved",
  },
  SEVERITY: {
    INFO: "eventAlertSeverityInfo",
    WARNING: "eventAlertSeverityWarning",
    CRITICAL: "eventAlertSeverityCritical",
  },
  TYPE: {
    DRUG_STOCK_LOW: {
      title: "eventAlertDrugStockLowTitle",
      message: "eventAlertDrugStockLowMessage",
    },
    DRUG_BATCH_EXPIRING: {
      title: "eventAlertDrugBatchExpiringTitle",
      message: "eventAlertDrugBatchExpiringMessage",
    },
    DRUG_BATCH_EXPIRED: {
      title: "eventAlertDrugBatchExpiredTitle",
      message: "eventAlertDrugBatchExpiredMessage",
    },
    FILTER_CAPACITY_WARNING: {
      title: "eventAlertFilterCapacityWarningTitle",
      message: "eventAlertFilterCapacityWarningMessage",
    },
    FILTER_EXHAUSTED: {
      title: "eventAlertFilterExhaustedTitle",
      message: "eventAlertFilterExhaustedMessage",
    },
    OPERATION_OVERDUE: {
      title: "eventAlertOperationOverdueTitle",
      message: "eventAlertOperationOverdueMessage",
    },
    ANALYSIS_OUT_OF_RANGE: {
      title: "eventAlertAnalysisOutOfRangeTitle",
      message: "eventAlertAnalysisOutOfRangeMessage",
    },
  },
  UNKNOWN_TYPE: {
    title: "eventAlertUnknownTitle",
    message: "eventAlertUnknownMessage",
  },
  // Generic placeholder reused whenever a specific entity name (drug_name,
  // filter_name, ...) is missing from the payload.
  UNKNOWN_ENTITY: "eventAlertUnknownEntity",

  // FE3 - Action Center (/alerts). Not present in the backend dictionary yet
  // either (same "#locale_area_key" sentinel until the next micro-patch).
  MENU: "eventAlertsMenu",
  PAGE_TITLE: "eventAlertsPageTitle",
  TAB: {
    OPEN: "eventAlertsTabOpen",
    ACKNOWLEDGED: "eventAlertsTabAcknowledged",
    RESOLVED: "eventAlertsTabResolved",
  },
  FILTER: {
    SEVERITY: "eventAlertsFilterSeverity",
    CATEGORY: "eventAlertsFilterCategory",
    TYPE: "eventAlertsFilterType",
    ENTITY_TYPE: "eventAlertsFilterEntityType",
    ASSIGNEE: "eventAlertsFilterAssignee",
  },
  ALL: "eventAlertsAll",
  DETECTED_AT: "eventAlertsDetectedAt",
  ASSIGNEE: "eventAlertsAssignee",
  UNASSIGNED: "eventAlertsUnassigned",
  ACTIONS: {
    ACKNOWLEDGE: "eventAlertsAcknowledge",
    CLAIM: "eventAlertsClaim",
    ASSIGN: "eventAlertsAssign",
    REASSIGN: "eventAlertsReassign",
    UNASSIGN: "eventAlertsUnassign",
    RESOLVE: "eventAlertsResolve",
  },
  RESOLUTION_REASON: "eventAlertsResolutionReason",
  RESOLUTION_REASON_REQUIRED: "eventAlertsResolutionReasonRequired",
  EMPTY_LIST: "eventAlertsEmptyList",
  PAGE_LOAD_ERROR: "eventAlertsPageLoadError",
  RETRY: "eventAlertsRetry",
  ACTION_ERROR: "eventAlertsActionError",
  RESOLVE_CONFIRM: "eventAlertsResolveConfirm",
  CANCEL: "eventAlertsCancel",
  // Display names for the closed enums accepted by GET /event-alerts
  // (verified against the real backend: eventAlertsQueryService.js for
  // category/severity, the *Normalizer.js files' literal `entityType` values
  // for entity_type, and this app's own known 7 detector types for `type` -
  // "type" has no closed catalog server-side yet, but no other type currently
  // exists end-to-end).
  CATEGORY_NAME: {
    OPERATIONS: "eventAlertsCategory_OPERATIONS",
    ANALYSIS: "eventAlertsCategory_ANALYSIS",
    WAREHOUSE: "eventAlertsCategory_WAREHOUSE",
    FILTER: "eventAlertsCategory_FILTER",
  },
  TYPE_NAME: {
    DRUG_STOCK_LOW: "eventAlertsTypeName_DRUG_STOCK_LOW",
    DRUG_BATCH_EXPIRING: "eventAlertsTypeName_DRUG_BATCH_EXPIRING",
    DRUG_BATCH_EXPIRED: "eventAlertsTypeName_DRUG_BATCH_EXPIRED",
    FILTER_CAPACITY_WARNING: "eventAlertsTypeName_FILTER_CAPACITY_WARNING",
    FILTER_EXHAUSTED: "eventAlertsTypeName_FILTER_EXHAUSTED",
    OPERATION_OVERDUE: "eventAlertsTypeName_OPERATION_OVERDUE",
    ANALYSIS_OUT_OF_RANGE: "eventAlertsTypeName_ANALYSIS_OUT_OF_RANGE",
  },
  ENTITY_TYPE_NAME: {
    OPERATION: "eventAlertsEntityType_OPERATION",
    EXAM: "eventAlertsEntityType_EXAM",
    DRUG: "eventAlertsEntityType_DRUG",
    DRUG_BATCH: "eventAlertsEntityType_DRUG_BATCH",
    FILTER: "eventAlertsEntityType_FILTER",
  },

  // FE4 - hierarchical configuration (winery settings + DRUG/FILTER
  // overrides). Not present in the backend dictionary yet either; will be
  // added together with the FE3 keys in the next combined micro-patch.
  CONFIGURATION: {
    SECTION_TITLE: "eventAlertConfigurations",
    EXPIRY_TITLE: "eventAlertExpiryConfiguration",
    FILTER_CAPACITY_TITLE: "eventAlertFilterCapacityConfiguration",
    // Extensions beyond the FE4 spec's minimum list: the entity-level
    // (product/filter) override headings use different wording than the
    // winery-level section ("Preavviso scadenza batch" / "Avviso capacità
    // filtro" vs "Scadenza drug batch" / "Capacità filtro" per the spec's own
    // text), so they get their own title keys instead of reusing
    // EXPIRY_TITLE/FILTER_CAPACITY_TITLE verbatim in a different context.
    DRUG_OVERRIDE_TITLE: "eventAlertDrugExpiryOverride",
    FILTER_OVERRIDE_TITLE: "eventAlertFilterCapacityOverride",
    MODE: "eventAlertConfigurationMode",
    INHERIT: "eventAlertConfigurationInherit",
    OVERRIDE: "eventAlertConfigurationOverride",
    DISABLED: "eventAlertConfigurationDisabled",
    EFFECTIVE_VALUE: "eventAlertConfigurationEffectiveValue",
    SOURCE: "eventAlertConfigurationSource",
    SOURCE_NAME: {
      APPLICATION_DEFAULT: "eventAlertConfigurationSourceApplicationDefault",
      WINERY_OVERRIDE: "eventAlertConfigurationSourceWineryOverride",
      WINERY_DISABLED: "eventAlertConfigurationSourceWineryDisabled",
      ENTITY_OVERRIDE: "eventAlertConfigurationSourceEntityOverride",
      ENTITY_DISABLED: "eventAlertConfigurationSourceEntityDisabled",
    },
    DAYS: "eventAlertConfigurationDays",
    PERCENT: "eventAlertConfigurationPercent",
    SAVE: "eventAlertConfigurationSave",
    RESET: "eventAlertConfigurationReset",
    LOAD_ERROR: "eventAlertConfigurationLoadError",
    SAVE_ERROR: "eventAlertConfigurationSaveError",
    SAVED: "eventAlertConfigurationSaved",
    INVALID_EXPIRY_DAYS: "eventAlertConfigurationInvalidExpiryDays",
    INVALID_CAPACITY_PERCENT: "eventAlertConfigurationInvalidCapacityPercent",
  },

  // FE5 - navigation from the Home widget and from host pages into the
  // Action Center. Not present in the backend dictionary yet; will be added
  // by a final two-key micro-patch (or reuse existing keys if a semantically
  // identical one is found - see FE5 report).
  VIEW_ALL: "eventAlertsViewAll",
  VIEW_RELATED: "eventAlertsViewRelated",
};
